<script setup>
import { ref, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import RewardAnimation from '@/components/RewardAnimation.vue'
import { IonModal, IonContent, IonButton, IonButtons, IonHeader, IonToolbar, IonTitle, IonSpinner } from '@ionic/vue'
import { useI18n } from 'vue-i18n'
import { showRewardInterstitialIntegrated, isApp, addUserCredits } from '@/helpers/utils.js'
import StreakIncreased from '@/components/StreakIncreased.vue'
import { el } from '@formkit/i18n'
import noAdsImage from '@/assets/images/icons/noAds.webp'


const router = useRouter()
const { t } = useI18n()

const messages = ref([])
const isOpen = ref(false)
const title = ref("")
const notification_type = ref("")
const body = ref("")
const should_animate = ref(false)
const reward_animation = ref(null)
const currentClosureCallback = ref(null)

// Web ad state
const showingWebAd = ref(false)
const webAdConfig = ref(null)
const adStatus = ref('loading')
const adLoaded = ref(false)
const retryCount = ref(0)
const canBackdropDismiss = ref(true)

function showMessage(message, optionalClosureCallback = null) {
  messages.value.push({
    ...message,
    closureCallback: optionalClosureCallback
  })
  showNextMessage()
}

async function showNextMessage() {
  console.log("showNextMessage")
  if (isOpen.value) {
    console.log("isOpen")
    return
  }
  const message = messages.value.shift()
  if (!message) {
    console.log("no message")
    return
  }
  console.log("showing message", message)
  notification_type.value = message.notification_type
  
  if (notification_type.value === 'flashcard_review_credits_used_up' || notification_type.value === 'chat_message_credits_used_up') {
    canBackdropDismiss.value = false
    var creditType
    if (notification_type.value === 'flashcard_review_credits_used_up') {
      creditType = 'flashcard_review'
    }
    else if (notification_type.value === 'chat_message_credits_used_up') {
      creditType = 'chat_message'
    }
    const result = await showRewardInterstitialIntegrated(creditType)
    console.log('result from showRewardInterstitialIntegrated:', result)
    
    if (result && result.type === 'web') {
      showingWebAd.value = true
      webAdConfig.value = result
      adStatus.value = 'loading'
      adLoaded.value = false
      retryCount.value = 0
      
      title.value = message.title
      body.value = message.body
      should_animate.value = message.meta.animation
      currentClosureCallback.value = message.closureCallback
      isOpen.value = true
      
      await nextTick()
      loadWebAd()
      return
    }
  }
  else {
    canBackdropDismiss.value = true
  }
  
  title.value = message.title
  body.value = message.body
  // console.log("reward is", reward_animation.value)
  console.log('title is ', title.value, ' and body is ', body.value)
  should_animate.value = message.meta.animation
  currentClosureCallback.value = message.closureCallback
  isOpen.value = true
}

function loadWebAd() {
  if (!webAdConfig.value) return
  
  const containerId = `ad-container-${webAdConfig.value.adKey}`
  
  webAdConfig.value.loadAd(containerId, {
    onStatusChange: (status) => {
      adStatus.value = status
      console.log('Ad status:', status)
    },
    onSuccess: () => {
      adLoaded.value = true
    },
    onUnfilled: () => {
      if (retryCount.value < webAdConfig.value.maxRetries) {
        retryCount.value++
        console.log(`Ad unfilled, retrying (${retryCount.value}/${webAdConfig.value.maxRetries})...`)
        webAdConfig.value.adKey++
        adStatus.value = 'loading'
        nextTick(() => loadWebAd())
      }
    },
    onTimeout: () => {
      adStatus.value = 'timeout'
      adLoaded.value = true
    },
    onError: (err) => {
      console.error('Ad loading error:', err)
      adStatus.value = 'error'
      adLoaded.value = true
    }
  })
}

async function close() {
  console.log('calling close')
  isOpen.value = false
  showingWebAd.value = false
  webAdConfig.value = null
  adLoaded.value = false
  adStatus.value = 'loading'
  currentClosureCallback.value = null
  setTimeout(() => {
    console.log("timer")
    showNextMessage()
  }, 500)
  await handleWebAdComplete()
}

function go_to_billing() {
  if (currentClosureCallback.value && typeof currentClosureCallback.value === 'function') {
    currentClosureCallback.value()
  }
  close()
  router.push({ name: 'BillingView' })
}

// Add a function to handle web ad completion
async function handleWebAdComplete() {
  // Add credits for web ad view
  console.log("handleWebAdComplete called")
  if (showingWebAd.value) {
  console.log("adding user credits")
    if (notification_type.value === 'flashcard_review_credits_used_up') {
      await addUserCredits('flashcard_review')
    }
    else if (notification_type.value === 'chat_message_credits_used_up') {
      await addUserCredits('chat_message')
    }
  }
  // Then hide the ad
  showingWebAd.value = false
}

async function canDismiss(data, role) {
  if (showingWebAd.value && webAdConfig.value && adStatus.value === 'loading') {
    console.log("Cannot dismiss while ad is loading")
    return false
  }
  else if (showingWebAd.value) {
    showingWebAd.value = false
    return false
  }
  return true
}

defineExpose({
  showMessage
})
</script>

<template>
  <ion-modal :is-open="isOpen" @didDismiss="close" :class="[ isApp ? 'is-in-app': 'not-in-app']" :can-dismiss="canDismiss">
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button v-if="showingWebAd && webAdConfig" @click="handleWebAdComplete">
            <i class="bi bi-x action-icon"></i> 
          </ion-button>
          <ion-button v-else @click="close">
            <i class="bi bi-x action-icon"></i> 
          </ion-button>
        </ion-buttons>
        <ion-title>{{ showingWebAd ? t('ads.sponsored-content') : title }}</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <!-- Web Ad Section -->
      <div v-if="showingWebAd && webAdConfig" style=" height: 500px !important;" class="argg">
        <!-- Test mode indicator -->
        <div v-if="webAdConfig.isTestMode" class="test-mode-banner">
          TEST MODE - Test ads enabled
        </div>
        
        <!-- Use key to force re-render of ad container -->
        <div :id="`ad-container-${webAdConfig.adKey}`" :key="webAdConfig.adKey" class="ad-container">
          <!-- Loading state -->
          <div v-if="adStatus === 'loading'" class="ad-placeholder">
            <ion-spinner name="circles"></ion-spinner>
            <p>{{ t('ads.loading') }}...</p>
          </div>
          
          <!-- Unfilled state (after max retries) -->
          <div v-else-if="adStatus === 'unfilled' && retryCount >= webAdConfig.maxRetries" class="ad-placeholder">
            <p>{{ t('ads.no-ads-available') }}</p>
            <p class="ad-info">{{ t('ads.please-continue') }}</p>
          </div>
          
          <!-- Error or timeout state -->
          <div v-else-if="adStatus === 'error' || adStatus === 'timeout'" class="ad-placeholder">
            <p>{{ t('ads.loading-error') }}</p>
            <p class="ad-info">{{ t('ads.please-continue') }}</p>
          </div>
          
          <!-- Ad container with test mode support -->
          <div style="width: 100%;">
            <ins class="adsbygoogle"
                 :style="{
                   display: 'block',
                   minHeight: '250px',
                   asdfvisibility: adStatus === 'filled' ? 'visible' : 'hidden'
                 }"
                 data-ad-client="ca-pub-6734004389316610"
                 data-ad-slot="4898069421"
                 data-ad-format="inline"
                 :data-adtest="webAdConfig.isTestMode ? 'on' : undefined"></ins>
          </div>
        </div>
        
        <div class="ad-footer">
          <button type="button" 
                  class="btn btn-primary btn-primary-cs"  
                  @click="handleWebAdComplete"
                  :disabled="adStatus === 'loading'">
            {{ t('billing.continue') || 'Continue' }}
          </button>
        </div>
      </div>
      
      <!-- Regular Message Content -->
      <div v-else class="d-flex flex-column h-100">
        <template v-if="notification_type === 'chat_message_credits_used_up' || notification_type === 'flashcard_review_credits_used_up'">
          <div class="modal-body">
            <span id="here">
              <div class="upsell-content">
                <h2 class="upsell-title">
                  {{ t('billing.learn-without-ads-with-a') }}
                  <span class="highlight-green">{{ t('billing.7-day-free-trial-of-lingo-ing') }}</span> 
                </h2>
                
                <div class="upsell-image-container mt-2">
                  <img
                    :src="noAdsImage" 
                    alt="Lingo.ing Premium - No Ads" 
                    class="upsell-image"
                  />
                </div>
                
                <div class="upsell-benefits mt-2 mb-2">
                  <div class="benefit-item">
                    <i class="bi bi-check-circle-fill text-success"></i>
                    <span>{{ t('billing.no-ads') }}</span>
                  </div>
                  <div class="benefit-item">
                    <i class="bi bi-check-circle-fill text-success"></i>
                    <span>{{ t('billing.unlimited-games') }}</span>
                  </div>
                  <div class="benefit-item">
                    <i class="bi bi-check-circle-fill text-success"></i>
                    <span>{{ t('billing.unlimited-chats') }}</span>
                  </div>
                </div>
                
                <p class="upsell-subtext">{{ body }}</p>
              </div>
            </span>
          </div>
          <div class="modal-footer">
            <div class="col-12">
            <button type="button" class="btn btn-primary btn-primary-cs"  @click="go_to_billing">
              {{ t('cards.subscribe-now') }}
            </button>
          </div>
            <div class="col-12 mt-2">
            <button type="button" class="btn btn-secondary btn-secondary-cs"  @click="close">
              {{ t('cards.no-thanks') }}
            </button>
          </div>
          </div>
        </template>
        <template v-else-if="notification_type === 'daily_challenge'">
          <streak-increased @close="close" />
        </template>
        <template v-else>
          <div class="modal-body">
            <span>
              {{ body }}
            </span>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary btn-primary-cs" @click="close">
              {{ t('cards.keep-studying') }}
            </button>
          </div>

        </template>
      </div>
      
      <reward-animation v-if="should_animate && !showingWebAd" ref="reward_animation">
      </reward-animation>
    </ion-content>
  </ion-modal>
</template>

<style scoped>
.modal-body {
  padding: 20px 0;
  font-size: 1.1em;
  line-height: 1.5;
}

.modal-footer {
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid var(--ion-color-light);
}

/* Web Ad Styles from WebRewardInterstitial */
.test-mode-banner {
  background-color: #ff9800;
  color: white;
  padding: 8px;
  text-align: center;
  font-size: 0.9em;
  font-weight: bold;
  margin-bottom: 10px;
  border-radius: 4px;
}

.argg-container {
  min-height: 300px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  position: relative;
  background-color: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}


.ad-container {
  min-height: 300px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  position: relative;
  background-color: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}

.ad-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #666;
  z-index: 1;
  width: 100%;
}

.ad-placeholder ion-spinner {
  margin-bottom: 10px;
}

.ad-info {
  font-size: 0.9em;
  margin-top: 10px;
  color: #999;
}

.ad-footer {
  margin-top: 20px;
}

:deep(.adsbygoogle) {
  width: 100%;
  max-width: 100%;
  z-index: 2;
}

:deep(ins.adsbygoogle) {
  background-color: transparent !important;
}

:deep(ins.adsbygoogle[data-ad-status="filled"]) {
  visibility: visible !important;
}

:deep(ins.adsbygoogle:not([data-ad-status="filled"])) {
  visibility: hidden !important;
}

/* Modal sizing */
/*  This needs to be disabled in app or it totally breaks everything */
ion-modal.not-in-app {
  --max-height: 100%;
  --width: 100% !important;
} 

.upsell-content {
  text-align: center;
  padding: 10px 0;
}

.upsell-title {
  font-size: 1.5rem;
  font-weight: bold;
  line-height: 1.3;
  margin-bottom: 20px;
  color: var(--ion-text-color);
}

.highlight-green {
  color: #28a745;
  background: linear-gradient(180deg, transparent 60%, rgba(40, 167, 69, 0.2) 60%);
  padding: 0 4px;
  border-radius: 4px;
}

.upsell-image-container {
  margin: 20px 0;
  display: flex;
  justify-content: center;
}

.upsell-image {
  max-width: 100%;
  height: auto;
  max-height: 200px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.upsell-benefits {
  margin: 20px 0;
  text-align: left;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 1rem;
}

.benefit-item i {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.upsell-subtext {
  margin-top: 20px;
  font-size: 0.95rem;
  color: var(--ion-color-medium);
  line-height: 1.4;
}

@media (max-width: 576px) {
  .upsell-title {
    font-size: 1.25rem;
  }
  
  .upsell-image {
    max-height: 150px;
  }
  
  .benefit-item {
    font-size: 0.9rem;
  }
}
</style>

